import { Link, useNavigate } from 'react-router-dom';
import { INVOICE_STATUSES, INVOICE_STATUS_LABELS, type InvoiceStatus } from '@faktorplus/shared';
import { useInvoiceList } from '../../api/invoices';
import { Button } from '../../components/ui/button';
import { EmptyState, ErrorBox, LoadingBlock, Panel } from '../../components/ui/feedback';
import { InvoiceStateBadge } from '../../components/ui/status-pill';
import { Select } from '../../components/ui/form';
import { AttachmentQuickView } from '../invoices/attachment-preview';
import { formatAmount, formatShortDate, plural } from '../../lib/format';
import pageStyles from '../../components/layout/page.module.css';
import styles from './projects.module.css';

const PAGE_SIZE = 20;

export interface ProjectInvoiceFilters {
  status?: InvoiceStatus;
  from?: string;
  to?: string;
  page: number;
}

interface ProjectInvoicesProps {
  projectId: string;
  filters: ProjectInvoiceFilters;
  onChange: (patch: Record<string, string>) => void;
}

/**
 * The site's invoices.
 *
 * Deliberately a short, read-only list rather than a second register: the
 * columns answer "what was booked here and where is it", and anything more
 * specific is one click away in the register itself, already filtered.
 */
export function ProjectInvoices({ projectId, filters, onChange }: ProjectInvoicesProps) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useInvoiceList({
    projectId,
    status: filters.status,
    issuedFrom: filters.from ? new Date(filters.from) : undefined,
    issuedTo: filters.to ? new Date(filters.to) : undefined,
    page: filters.page,
    pageSize: PAGE_SIZE,
    sortBy: 'issueDate',
    sortDir: 'desc',
  });

  const invoices = data?.items ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Panel
      title={`Faktury na tej budowie — ${total} ${plural(total, ['faktura', 'faktury', 'faktur'])}`}
      action={
        <div className={styles.panelActions}>
          <Select
            compact
            className={styles.statusFilter}
            aria-label="Status faktury"
            value={filters.status ?? ''}
            onChange={(event) => onChange({ status: event.target.value, page: '' })}
          >
            <option value="">Wszystkie statusy</option>
            {INVOICE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {INVOICE_STATUS_LABELS[status]}
              </option>
            ))}
          </Select>
          <Link className={styles.panelLink} to={`/faktury?projectId=${projectId}`}>
            Otwórz w rejestrze →
          </Link>
        </div>
      }
    >
      {error && <ErrorBox>Nie udało się pobrać faktur tej budowy.</ErrorBox>}
      {isLoading && <LoadingBlock />}

      {!isLoading && !error && invoices.length === 0 && (
        <EmptyState
          mark="∅"
          title="Brak faktur"
          description="Żadna faktura nie pasuje do wybranego okresu i statusu."
        />
      )}

      {invoices.length > 0 && (
        <div className={pageStyles.tableWrap}>
          <table className={pageStyles.table}>
            <thead>
              <tr>
                <th>Numer</th>
                <th className={pageStyles.hideOnPhone}>Wystawiona</th>
                <th>Kontrahent</th>
                <th className={pageStyles.hideOnPhone}>Kategoria</th>
                <th className={`${pageStyles.numeric} ${pageStyles.hideOnPhone}`}>Netto</th>
                <th className={pageStyles.numeric}>Brutto</th>
                <th className={pageStyles.hideOnPhone}>Termin</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  onClick={() => void navigate(`/faktury/${invoice.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className={pageStyles.docNumber}>{invoice.number}</td>
                  <td className={`${pageStyles.muted} ${pageStyles.hideOnPhone}`}>
                    {formatShortDate(invoice.issueDate)}
                  </td>
                  <td className={pageStyles.strong}>{invoice.vendorName}</td>
                  <td className={`${pageStyles.muted} ${pageStyles.hideOnPhone}`}>
                    {invoice.categoryName ?? '—'}
                  </td>
                  <td className={`${pageStyles.numeric} ${pageStyles.hideOnPhone}`}>
                    {formatAmount(invoice.netAmount)}
                  </td>
                  <td className={`${pageStyles.numeric} ${pageStyles.strong}`}>
                    {formatAmount(invoice.grossAmount)}
                  </td>
                  <td className={`${pageStyles.muted} ${pageStyles.hideOnPhone}`}>
                    {formatShortDate(invoice.dueDate)}
                  </td>
                  <td>
                    <InvoiceStateBadge invoice={invoice} short />
                  </td>
                  <td>
                    {/* The scan one click away, same as in payables — deciding
                        whether a site cost looks right keeps needing it
                        (client request of 24.09.2026). */}
                    <div className={pageStyles.rowActions}>
                      {invoice.attachments[0] && (
                        <AttachmentQuickView
                          attachment={invoice.attachments[0]}
                          label={invoice.number}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lastPage > 1 && (
        <div className={pageStyles.pagination}>
          <Button
            disabled={filters.page <= 1}
            onClick={() => onChange({ page: String(filters.page - 1) })}
          >
            ← Poprzednia
          </Button>
          <span>
            Strona {filters.page} z {lastPage}
          </span>
          <span className={pageStyles.paginationSpacer} />
          <Button
            disabled={filters.page >= lastPage}
            onClick={() => onChange({ page: String(filters.page + 1) })}
          >
            Następna →
          </Button>
        </div>
      )}
    </Panel>
  );
}
